import { useDispatch, useSelector } from "react-redux";
import {
  BackButton,
  CancelButton,
  CommentContainer,
  EditorButtonsContainer,
  Header,
  NewCommentButton,
  NewCommentButtonIcon,
  NewCommentContainer,
  NewCommentLabel,
  PublishButton,
  QuillContainer,
  Subtitle,
  Title,
} from "./index.styled";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { AppState } from "../../../../store/app-state";
import { RecruiterCommentInterface } from "../../../../models/recruiter-comment.model";
import {
  fetchRecruiterComments,
  saveRecruiterComment,
  updateRecruiterComment,
} from "../../../../store/event/event-actions";
import { FiArrowLeft } from "react-icons/fi";
import { EditButton } from "../comment/index.styled";
import { MdAddCircleOutline, MdEdit } from "react-icons/md";
import ReactQuill from "react-quill";
import { format } from "date-fns";
import Comment from "../comment";
import "react-quill/dist/quill.snow.css";
import useUmamiTracking from "../../../../hooks/use-umami-tracking";
import { Events } from "../../../../utils/constants";

interface CommentsProps {
  candidateId?: string;
}

const Comments: React.FC<CommentsProps> = ({ candidateId }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("private-home-page");
  const [detail, setDetail] = useState<RecruiterCommentInterface | null>(null);
  const [editing, setEditing] = useState<boolean>(false);
  const { recruiterComments } = useSelector(
    (state: AppState) => state.eventState
  );
  const user = useSelector((state: AppState) => state.authState.user);

  const CLEAN_COMMENT: RecruiterCommentInterface = {
    id: "",
    comment: "",
    editable: true,
    last_modified: "",
    author: {
      id: "",
      name: "",
    },
  };

  const commentsLenght = useMemo((): number => {
    return recruiterComments.data ? recruiterComments.data.length : 0;
  }, [recruiterComments]);
  const pushUmami = useUmamiTracking();

  const saveComment = useCallback(() => {
    if (candidateId && detail && !detail.id) {
      dispatch(saveRecruiterComment(candidateId, detail.comment));
    } else if (detail) {
      dispatch(updateRecruiterComment(detail.comment, detail.id));
    }
    setTimeout(() => dispatch(fetchRecruiterComments(candidateId ?? "")), 500);
    setEditing(false);
    pushUmami(
      Events.ADD_COMMENT,
      { comment: detail?.comment ?? "", candidateId },
      user
    );
  }, [dispatch, candidateId, detail, user, pushUmami]);

  const cancelComment = useCallback(() => {
    setEditing(false);
    if (detail?.id === "") {
      setDetail(null);
    }
  }, [detail]);

  const errorDescription = useMemo(() => {
    if (!detail) return true;
    const description = detail?.comment.replace(/<[^>]*>?/gm, "");
    return description.trim().length === 0;
  }, [detail]);

  const handleEdit = useCallback((c, edit) => {
    setDetail(c);
    setEditing(edit);
  }, []);

  const getDate = useCallback(
    (comment) => {
      if (!comment || comment.last_modified === "") return "";
      const last_modified = new Date(comment.last_modified);
      return i18n.language === "en"
        ? format(last_modified, "MM/dd/yyyy HH:mm")
        : format(last_modified, "dd/MM/yyyy HH:mm");
    },
    [i18n]
  );
  return (
    <>
      <Header>
        <Title>
          {!editing
            ? t("talent.comment", { count: commentsLenght })
            : t("talent.considerations")}
        </Title>
        {!detail && commentsLenght > 0 && (
          <NewCommentButton onClick={() => handleEdit(CLEAN_COMMENT, true)}>
            {t("talent.addComment")}
          </NewCommentButton>
        )}
      </Header>

      <Subtitle>{t("talent.comment-info")}</Subtitle>

      {!!detail ? (
        <QuillContainer editing={editing}>
          <div className="header">
            <BackButton onClick={() => handleEdit(null, false)}>
              <FiArrowLeft />
              {t("back")}
            </BackButton>
            {detail.editable && (
              <EditButton onClick={() => setEditing(true)}>
                <MdEdit />
              </EditButton>
            )}
          </div>
          <ReactQuill
            readOnly={!editing}
            theme="snow"
            value={detail.comment}
            onChange={(value: string) =>
              setDetail({ ...detail, comment: value })
            }
          />
          {detail && detail.author.name && (
            <div className="footer">
              {t("talent.last-edited", {
                date: getDate(detail),
                author: detail.author.name,
              })}
            </div>
          )}
        </QuillContainer>
      ) : commentsLenght > 0 ? (
        <CommentContainer>
          {recruiterComments.data?.map((c) => (
            <Comment
              handleEdit={() => handleEdit(c, true)}
              setDetail={() => handleEdit(c, false)}
              comment={c}
              lastEdited={t("talent.last-edited", {
                date: getDate(c),
                author: c.author.name,
              })}
            />
          ))}
        </CommentContainer>
      ) : (
        <NewCommentContainer>
          <NewCommentButtonIcon onClick={() => handleEdit(CLEAN_COMMENT, true)}>
            <MdAddCircleOutline />
          </NewCommentButtonIcon>
          <NewCommentLabel>{t("talent.addNewComment")}</NewCommentLabel>
        </NewCommentContainer>
      )}

      {!!detail && editing && (
        <EditorButtonsContainer>
          <CancelButton onClick={cancelComment}>
            {t("talent.cancel")}
          </CancelButton>
          <PublishButton onClick={saveComment} disabled={errorDescription}>
            {detail.id === ""
              ? t("talent.publishComment")
              : t("talent.updateComment")}
          </PublishButton>
        </EditorButtonsContainer>
      )}
    </>
  );
};

export default Comments;
